import * as React from "react";
import * as _ from "lodash";
import { Space, Spin, List } from "antd";

import { FarmEntity, GamePlanEntity, TodoItemEntity, TodoUpdateAction } from "@ctra/api";
import styles from "./TaskList.module.less";
import Task, { TaskVariant } from "../Task";

const { TaskList: TaskListStyles, List: ListStyles } = styles;

/**
 * Reference of task ids: either a single id or a list of ids
 */
type TaskIDReference = TodoItemEntity["id"] | Array<TodoItemEntity["id"]>;

export interface TaskListProps {
  /**
   * game plan tasks (with farm names)
   */
  items: Array<
    TodoItemEntity & {
      farm?: FarmEntity["name"];
      gamePlan?: GamePlanEntity["title"];
    }
  >;
  /**
   * render extra for each task
   * @param task
   */
  extra?: (task: TodoItemEntity, loading: boolean) => React.ReactNode;
  /**
   * render the timestamp for each task
   * @param task
   * @param loading
   */
  renderTimestamp?: (task: TodoItemEntity, loading: boolean) => React.ReactNode;
  /**
   * empty state
   */
  empty?: React.ReactNode;
  /**
   * handle change
   */
  onChange?: (task: TodoItemEntity, action: TodoUpdateAction) => void;
  /**
   * list variant
   */
  variant: TaskVariant;
  /**
   * loading state (boolean for the whole list, TodoItemEntity["id"] or Array<TodoItemEntity["id"]>)
   */
  loading?: boolean | TaskIDReference;
  /**
   * inactive state, it does not respond to clicks
   */
  inactive?: boolean;
  /**
   * whether to show pagination or not
   */
  withPagination?: boolean;
}

/**
 * Game plan task list (inline or expanded)
 * @param items
 * @param onChange
 * @param loading
 * @param variant
 * @param inactive
 * @param extra
 * @param renderTimestamp
 * @param empty
 * @param withPagination
 * @constructor
 */
const TaskList: React.FC<TaskListProps> = ({
  items,
  onChange = _.noop,
  loading = false,
  variant = TaskVariant.Expanded,
  inactive = false,
  extra = () => null,
  renderTimestamp = () => null,
  empty = null,
  withPagination = false
}) => {
  /**
   * Tell whether the given item is loading
   * @param item
   * @param loading
   */
  const isItemLoading = (item: TodoItemEntity, loading: TodoItemEntity["id"] | Array<TodoItemEntity["id"]>) =>
    _.isArray(loading) ? loading.includes(item.id) : loading === item.id;

  /**
   * Render the task
   * @param item
   */
  const renderTask = (item: TodoItemEntity) => {
    const isLoading = isItemLoading(item, loading as TaskIDReference);

    return (
      <Task
        extra={extra(item, isLoading)}
        variant={variant}
        onChange={inactive ? _.noop : _.partial(onChange, item)}
        loading={isLoading}
        key={item.id}
        {...item}
      >
        {renderTimestamp(item, isLoading)}
      </Task>
    );
  };

  /**
   * Inline lists have no empty state
   */
  const emptyState = variant === TaskVariant.Inline ? null : empty;

  const defaultPageSize = 4;

  return (
    <Spin spinning={_.isBoolean(loading) && loading}>
      {items.length ? (
        variant === TaskVariant.Inline ? (
          <Space direction="horizontal" size="small" className={TaskListStyles}>
            {items.map(renderTask)}
          </Space>
        ) : (
          <List
            className={ListStyles}
            dataSource={items}
            renderItem={renderTask}
            pagination={
              withPagination &&
              !!items.length && {
                size: "small",
                defaultPageSize,
                total: items.length,
                disabled: items.length <= defaultPageSize,
                showSizeChanger: items.length > defaultPageSize,
                pageSizeOptions: _.times(3, (i) => (defaultPageSize * (i + 1)).toString())
              }
            }
          />
        )
      ) : (
        emptyState
      )}
    </Spin>
  );
};

export default TaskList;
